import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Button from "@mui/material/Button";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import PrintIcon from "@mui/icons-material/Print";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import { assessmentAPI } from "../../services/api";
import { tokenManager } from "../../services/tokenManager";

const AssessmentResults = () => {
  const navigate = useNavigate();
  const { assessmentId } = useParams();
  const location = useLocation();
  
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [score, setScore] = useState(0);
  const [riskLevel, setRiskLevel] = useState("Low");
  const [riskColor, setRiskColor] = useState("bg-green-100 text-green-800");

  // Calculate ADHD score from symptoms
  const calculateScore = (symptoms) => {
    if (!symptoms) return 0;
    
    let totalScore = 0;
    let questionCount = 0;
    
    // Score mapping: Never=0, Rarely=1, Sometimes=2, Often=3, Very Often=4
    const scoreMap = {
      "Never": 0,
      "Rarely": 1,
      "Sometimes": 2,
      "Often": 3,
      "Very Often": 4
    };
    
    // Calculate inattention score (8 questions)
    for (let i = 0; i < 8; i++) {
      const questionKey = `inattention_${i}`;
      if (symptoms[questionKey]) {
        totalScore += scoreMap[symptoms[questionKey]] || 0;
        questionCount++;
      }
    }
    
    // Calculate hyperactivity score (8 questions)
    for (let i = 0; i < 8; i++) {
      const questionKey = `hyperactivity_${i}`;
      if (symptoms[questionKey]) {
        totalScore += scoreMap[symptoms[questionKey]] || 0;
        questionCount++;
      }
    }
    
    // Convert to percentage (max score is 64, so multiply by 100/64)
    const percentage = questionCount > 0 ? Math.round((totalScore / questionCount) * 25) : 0;
    return Math.min(percentage, 100);
  };

  // Determine risk level based on score
  const determineRiskLevel = (score) => {
    if (score >= 70) {
      return { level: "High", color: "bg-red-100 text-red-800" };
    } else if (score >= 40) {
      return { level: "Moderate", color: "bg-yellow-100 text-yellow-800" };
    } else {
      return { level: "Low", color: "bg-green-100 text-green-800" };
    }
  };

  // Get risk level description
  const getRiskDescription = (level) => {
    switch (level) {
      case "High":
        return "Your answers indicate significant ADHD-related symptoms. Professional evaluation is strongly recommended.";
      case "Moderate":
        return "Your answers indicate moderate ADHD-related symptoms. This score suggests that you may benefit from professional evaluation and support.";
      case "Low":
        return "Your answers indicate minimal ADHD-related symptoms. However, if you have concerns, consulting with a professional is always recommended.";
      default:
        return "Your assessment results are being processed.";
    }
  };

  // Fetch assessment data
  useEffect(() => {
    async function fetchAssessmentData() {
      setLoading(true);
      setError(null);
      
      try {
        let response;
        
        // If we have an assessmentId in URL, fetch that specific assessment
        if (assessmentId) {
          response = await assessmentAPI.getAssessmentById(assessmentId);
          setAssessmentData(response.assessment);
        } else {
          // For patient mode, get current user and fetch their assessment
          const currentUser = tokenManager.getUser();
          const userId = currentUser?._id || currentUser?.id;
          
          if (!userId) {
            setError('User not found. Please log in again.');
            setLoading(false);
            return;
          }
          
          response = await assessmentAPI.getAssessmentByPatientId(userId);
          
          // Handle different response structures
          if (response.data && response.data.assessments && response.data.assessments.length > 0) {
            setAssessmentData(response.data.assessments[0]);
          } else if (response.assessment) {
            setAssessmentData(response.assessment);
          } else if (response.data && response.data.assessment) {
            setAssessmentData(response.data.assessment);
          } else {
            setError('No assessment found. Please complete an assessment first.');
            setLoading(false);
            return;
          }
        }
        
      } catch (err) {
        console.error('Error fetching assessment data:', err);
        setError('Failed to load assessment data. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    
    fetchAssessmentData();
  }, [assessmentId]);

  // Calculate score when assessment data changes
  useEffect(() => {
    if (assessmentData?.responses?.symptoms) {
      const calculatedScore = calculateScore(assessmentData.responses.symptoms);
      setScore(calculatedScore);
      
      const risk = determineRiskLevel(calculatedScore);
      setRiskLevel(risk.level);
      setRiskColor(risk.color);
    }
  }, [assessmentData]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg w-full mx-auto p-8">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
          <div className="h-32 bg-gray-200 rounded mb-8"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg w-full mx-auto p-8">
        <div className="text-center text-red-500">
          <p className="text-lg font-semibold mb-2">Error Loading Assessment</p>
          <p className="text-sm">{error}</p>
          <Button
            variant="contained"
            onClick={() => navigate('/assessment')}
            sx={{ mt: 2 }}
          >
            Take Assessment
          </Button>
        </div>
      </div>
    );
  }

  if (!assessmentData) {
    return (
      <div className="bg-white rounded-lg w-full mx-auto p-8">
        <div className="text-center">
          <p className="text-lg font-semibold mb-2">No Assessment Found</p>
          <p className="text-sm text-gray-600 mb-4">
            You haven't completed an assessment yet.
          </p>
          <Button
            variant="contained"
            onClick={() => navigate('/assessment')}
          >
            Take Assessment
          </Button>
        </div>
      </div>
    );
  }

  const responses = assessmentData.responses || {};
  const basicInfo = responses.basicInfo || {};
  const symptoms = responses.symptoms || {};
  const impact = responses.impact || {};
  const history = responses.History || responses.history || {};

  return (
    <div className="bg-white rounded-lg w-full mx-auto">
      {/* Assessment Complete Banner */}
      <div className="bg-blue-50 flex items-center p-5 rounded-lg mb-6 w-full">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 mr-4">
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </span>
        <div>
          <div className="text-base font-semibold text-gray-900">
            Assessment Complete
          </div>
          <div className="text-sm text-gray-700 mt-0.5">
            Completed on {new Date(assessmentData.updatedAt || Date.now()).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Score and Radar Chart */}
      <div className="bg-white rounded-xl shadow-md p-5 flex w-full mb-8">
        <div className="flex flex-col items-start w-1/2 pr-8">
          <span className={`inline-block ${riskColor} text-xs font-semibold px-3 py-1 rounded-full mb-2`}>
            {riskLevel} Risk Level
          </span>
          <div className="flex items-end mb-1">
            <span className="text-5xl font-bold text-gray-900 leading-none">
              {score}
            </span>
            <span className="text-xl font-normal text-gray-400 ml-1 mb-1">
              /100
            </span>
          </div>
          <div className="text-gray-500 mt-1 w-full text-sm">
            {getRiskDescription(riskLevel)}
          </div>
        </div>
        <div className="flex items-center justify-center w-1/2">
          <img
            src="/images/executive_function.png"
            alt="Radar Chart"
            className="rounded"
            style={{ minWidth: 160, minHeight: 160 }}
          />
        </div>
      </div>

      {/* Accordion Sections */}
      <div className="mb-4">
        {[
          {
            title: "Basic Information",
            subtitle: "Personal details and background information provided during the assessment.",
            content: (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Age Range:</span>
                    <p className="text-sm text-gray-900">{basicInfo.age || "Not provided"}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Gender:</span>
                    <p className="text-sm text-gray-900">{basicInfo.gender || "Not provided"}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Language:</span>
                    <p className="text-sm text-gray-900">{basicInfo.language || "Not provided"}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Timezone:</span>
                    <p className="text-sm text-gray-900">{basicInfo.timezone || "Not provided"}</p>
                  </div>
                </div>
              </div>
            )
          },
          {
            title: "Symptom Assessment",
            subtitle: "Detailed breakdown of symptoms and their severity levels.",
            content: (
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Inattention Symptoms</h4>
                  <div className="space-y-2">
                    {Array.from({ length: 8 }, (_, i) => {
                      const questionKey = `inattention_${i}`;
                      const response = symptoms[questionKey];
                      if (!response) return null;
                      
                      return (
                        <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-700">
                            {getInattentionQuestion(i)}
                          </span>
                          <span className="text-sm font-medium text-blue-600">
                            {response}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Hyperactivity/Impulsivity Symptoms</h4>
                  <div className="space-y-2">
                    {Array.from({ length: 8 }, (_, i) => {
                      const questionKey = `hyperactivity_${i}`;
                      const response = symptoms[questionKey];
                      if (!response) return null;
                      
                      return (
                        <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm text-gray-700">
                            {getHyperactivityQuestion(i)}
                          </span>
                          <span className="text-sm font-medium text-blue-600">
                            {response}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )
          },
          {
            title: "Impact Analysis",
            subtitle: "How ADHD symptoms affect daily life and functioning.",
            content: (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Work Performance Impact:</span>
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${impact.workPerformance || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">{impact.workPerformance || 0}%</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Social Relationships Impact:</span>
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${impact.socialRelationships || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">{impact.socialRelationships || 0}%</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Sleep & Daily Activities Impact:</span>
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${impact.sleepActivities || 0}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">{impact.sleepActivities || 0}%</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Emotional Regulation:</span>
                    <p className="text-sm text-gray-900 mt-1">{impact.emotionalRegulation || "Not provided"}</p>
                  </div>
                </div>
              </div>
            )
          },
          {
            title: "History & Background",
            subtitle: "Previous experiences and relevant medical history.",
            content: (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Previously Diagnosed with ADHD:</span>
                    <p className="text-sm text-gray-900">{history.diagnosedADHD || "Not provided"}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">History of Anxiety/Depression:</span>
                    <p className="text-sm text-gray-900">{history.anxietyDepression || "Not provided"}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Current Medications:</span>
                    <p className="text-sm text-gray-900">
                      {history.medications === "Yes" && history.medicationsDetails 
                        ? history.medicationsDetails 
                        : history.medications || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Allergies:</span>
                    <p className="text-sm text-gray-900">
                      {history.allergies === "Yes" && history.allergiesDetails 
                        ? history.allergiesDetails 
                        : history.allergies || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">History of Self-Harm:</span>
                    <p className="text-sm text-gray-900">{history.selfHarm || "Not provided"}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">Substance Use:</span>
                    <p className="text-sm text-gray-900">{history.substanceUse || "Not provided"}</p>
                  </div>
                </div>
              </div>
            )
          },
        ].map((section, idx) => (
          <Accordion
            key={section.title}
            elevation={2}
            sx={{
              backgroundColor: "#fff",
              borderRadius: 3,
              mb: idx !== 3 ? 2.5 : 0,
              boxShadow: "0 2px 8px rgba(60, 72, 88, 0.07)",
              border: "none",
              "&:before": { display: "none" },
              transition: "box-shadow 0.2s",
              "&:hover": {
                boxShadow: "0 4px 16px rgba(60, 72, 88, 0.12)",
              },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight={600} fontSize={16}>
                {section.title}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography color="text.secondary" fontSize={14} sx={{ mb: 2 }}>
                {section.subtitle}
              </Typography>
              {section.content}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>

      {/* Action Buttons */}
      {/* <div className="flex gap-3 mb-6">
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          sx={{ 
            borderRadius: 2, 
            minWidth: 200, 
            fontWeight: 500,
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Download PDF Report
        </Button>
        <Button
          variant="outlined"
          startIcon={<ShareIcon />}
          sx={{ 
            borderRadius: 2, 
            minWidth: 200, 
            fontWeight: 500,
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Share with Doctor
        </Button>
        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          sx={{ 
            borderRadius: 2, 
            minWidth: 200, 
            fontWeight: 500,
            color: '#1976d2',
            borderColor: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)'
            }
          }}
        >
          Print Results
        </Button>
      </div> */}

      {/* What Happens Next */}
      <div className="bg-white rounded-lg shadow p-5 mb-4 w-full">
        <div className="font-semibold text-lg mb-1 text-gray-900 ">
          What Happens Next?
        </div>
        <div className="text-gray-600 text-sm mb-4">
          A psychiatrist will review your results. If you haven't booked a
          consultation yet, you can do so below.
        </div>
        <Button
          variant="contained"
          startIcon={<EventAvailableIcon />}
          onClick={() => navigate('/doctors')}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            fontWeight: 500,
            mb: 1,
            backgroundColor: '#3b82f6',
            '&:hover': {
              backgroundColor: '#2563eb'
            }
          }}
        >
          Book Consultation
        </Button>

        <div>
          <a
            href="/dashboard"
            className="text-blue-600 text-sm mt-2 inline-block hover:underline"
            style={{ textDecoration: "none" }}
          >
            Return to Dashboard
          </a>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-blue-50 flex items-center p-3 rounded text-xs text-blue-700 mt-4">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 mr-3 text-lg">
          <span role="img" aria-label="brain">
            🧠
          </span>
        </span>
        <span>
          This is not a diagnosis. Your responses will be reviewed by a licensed
          mental health professional. You're not alone — we're here to support you.
        </span>
      </div>
    </div>
  );
};

// Helper functions to get question text
const getInattentionQuestion = (index) => {
  const questions = [
    "I have trouble sustaining attention in tasks or play activities",
    "I often lose things necessary for tasks or activities",
    "I frequently forget to complete tasks",
    "I have difficulty following through on instructions",
    "I often avoid tasks that require sustained mental effort",
    "I struggle to organize tasks and activities",
    "I become easily distracted by unrelated stimuli",
    "I often forget important appointments or deadlines",
  ];
  return questions[index] || "Question not found";
};

const getHyperactivityQuestion = (index) => {
  const questions = [
    "I often fidget or tap my hands/feet",
    "I have difficulty staying seated when expected to",
    "I often talk excessively",
    "I have trouble waiting my turn",
    "I often interrupt others",
    "I frequently engage in physical activities at inappropriate times",
    "I have difficulty playing or engaging in leisure activities quietly",
    "I often blurt out answers before questions are completed",
  ];
  return questions[index] || "Question not found";
};

export default AssessmentResults;
